
import React, { useState, useMemo } from 'react';
import { format } from 'date-fns';
import { Plus, Trash2, Building2, Download, Loader2, Check } from 'lucide-react';
import Layout from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useExchangeRates, CreateExchangeRateInput, CURRENCY_PAIRS, RATE_TYPES } from '@/hooks/finance/useExchangeRates';
import { Badge } from '@/components/ui/badge';
import { useBankAccounts, useUpdateBankAccount } from '@/hooks/use-bank-accounts';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';

// Frankfurter API supported pairs (no ETB)
const FRANKFURTER_BASES = ['USD', 'EUR', 'GBP'] as const;

interface FetchedRate {
  pair: string;
  rate: number;
  selected: boolean;
}

export default function ExchangeRatesSettingsPage() {
  const [filterCurrency, setFilterCurrency] = useState<string>('');
  const [filterRateType, setFilterRateType] = useState<string>('');
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [fetchDialogOpen, setFetchDialogOpen] = useState(false);
  const [fetchDate, setFetchDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [fetchedRates, setFetchedRates] = useState<FetchedRate[]>([]);
  const [isFetching, setIsFetching] = useState(false);
  const [isImporting, setIsImporting] = useState(false);

  const { rates, isLoading, createRate, isCreating, deleteRate } = useExchangeRates({
    currency_pair: filterCurrency || undefined,
    rate_type: filterRateType || undefined,
    date_from: dateFrom || undefined,
    date_to: dateTo || undefined,
  });

  const { data: bankAccountsList, isLoading: bankAccountsLoading } = useBankAccounts();
  const updateBankAccount = useUpdateBankAccount();

  const handleRateTypeChange = async (accountId: string, newRateType: string) => {
    await updateBankAccount.mutateAsync({ id: accountId, exchange_rate_type: newRateType });
  };

  const bankAccounts = bankAccountsList?.filter(ba => ba.is_active) || [];

  const [form, setForm] = useState<CreateExchangeRateInput>({
    currency_pair: 'USD/ETB',
    rate_type: 'bank_rate',
    rate: 0,
    effective_date: format(new Date(), 'yyyy-MM-dd'),
    bank_account_id: null,
    notes: null,
  });

  // Derive the quote currency from the selected pair
  const quoteCurrency = form.currency_pair.split('/')[1] || 'ETB';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (form.rate <= 0) return;
    await createRate({
      ...form,
      bank_account_id: form.bank_account_id || null,
    });
    setDialogOpen(false);
    setForm(prev => ({ ...prev, rate: 0, notes: null }));
  };

  const rateTypeLabel = (value: string) =>
    RATE_TYPES.find(r => r.value === value)?.label || value;

  // Fetch rates from Frankfurter API
  const handleFetchRates = async () => {
    setIsFetching(true);
    setFetchedRates([]);
    try {
      const results: FetchedRate[] = [];

      for (const base of FRANKFURTER_BASES) {
        const others = FRANKFURTER_BASES.filter(c => c !== base);
        const url = `https://api.frankfurter.dev/v1/${fetchDate}?base=${base}&symbols=${others.join(',')}`;
        const resp = await fetch(url);
        if (!resp.ok) throw new Error(`API error: ${resp.status}`);
        const data = await resp.json();

        for (const [symbol, rate] of Object.entries(data.rates as Record<string, number>)) {
          const pair = `${base}/${symbol}`;
          // Avoid duplicate inverse pairs
          if (!results.some(r => r.pair === pair)) {
            results.push({ pair, rate, selected: true });
          }
        }
      }

      setFetchedRates(results);
    } catch (err: any) {
      toast.error('Failed to fetch rates: ' + err.message);
    } finally {
      setIsFetching(false);
    }
  };

  const handleImportRates = async () => {
    const selected = fetchedRates.filter(r => r.selected);
    if (selected.length === 0) return;

    setIsImporting(true);
    let imported = 0;
    let errors = 0;

    for (const r of selected) {
      try {
        await createRate({
          currency_pair: r.pair,
          rate_type: 'bank_rate',
          rate: r.rate,
          effective_date: fetchDate,
          notes: 'Fetched from Frankfurter API (ECB)',
        });
        imported++;
      } catch {
        errors++;
      }
    }

    setIsImporting(false);
    setFetchDialogOpen(false);
    setFetchedRates([]);

    if (errors > 0) {
      toast.warning(`Imported ${imported} rates, ${errors} failed (may already exist)`);
    } else {
      toast.success(`Imported ${imported} rates`);
    }
  };

  const toggleFetchedRate = (pair: string) => {
    setFetchedRates(prev => prev.map(r => r.pair === pair ? { ...r, selected: !r.selected } : r));
  };

  // Build horizontal pivot
  const pivotData = useMemo(() => {
    const rowMap = new Map<string, Map<string, { rate: number; id: string }>>();
    
    for (const rate of rates) {
      const rowKey = `${rate.currency_pair}|${rate.rate_type}`;
      if (!rowMap.has(rowKey)) rowMap.set(rowKey, new Map());
      rowMap.get(rowKey)!.set(rate.effective_date, { rate: Number(rate.rate), id: rate.id });
    }

    const sortedRows = Array.from(rowMap.keys()).sort();
    const dateSet = new Set<string>();
    for (const rate of rates) dateSet.add(rate.effective_date);
    const sortedDates = Array.from(dateSet).sort();

    return { rowMap, sortedRows, sortedDates };
  }, [rates]);

  const rowLabel = (rowKey: string) => {
    const [pair, type] = rowKey.split('|');
    const typeLabel = RATE_TYPES.find(r => r.value === type)?.label || type;
    return `${typeLabel} ${pair}`;
  };

  return (
    <Layout>
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Exchange Rates</h1>
          <p className="text-muted-foreground text-sm">
            Manage historical exchange rates for multi-currency cash flow reporting
          </p>
        </div>
        <div className="flex gap-2">
          {/* Fetch from API Dialog */}
          <Dialog open={fetchDialogOpen} onOpenChange={setFetchDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline"><Download className="h-4 w-4 mr-2" /> Fetch from API</Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>Fetch Exchange Rates</DialogTitle>
                <DialogDescription>
                  Fetch rates from the European Central Bank (ECB) via Frankfurter API. Covers USD, EUR, GBP cross-rates. ETB pairs are not available — enter manually.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div className="flex gap-3 items-end">
                  <div className="space-y-2 flex-1">
                    <Label>Date</Label>
                    <Input
                      type="date"
                      value={fetchDate}
                      onChange={e => setFetchDate(e.target.value)}
                    />
                  </div>
                  <Button onClick={handleFetchRates} disabled={isFetching}>
                    {isFetching ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Fetching...</> : 'Fetch Rates'}
                  </Button>
                </div>

                {fetchedRates.length > 0 && (
                  <>
                    <div className="border rounded-lg">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-10"></TableHead>
                            <TableHead>Pair</TableHead>
                            <TableHead className="text-right">Rate</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {fetchedRates.map(r => (
                            <TableRow key={r.pair}>
                              <TableCell>
                                <Checkbox
                                  checked={r.selected}
                                  onCheckedChange={() => toggleFetchedRate(r.pair)}
                                />
                              </TableCell>
                              <TableCell className="font-medium">{r.pair}</TableCell>
                              <TableCell className="text-right font-mono">
                                {r.rate.toFixed(6)}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">
                        {fetchedRates.filter(r => r.selected).length} of {fetchedRates.length} selected
                      </span>
                      <Button onClick={handleImportRates} disabled={isImporting || fetchedRates.filter(r => r.selected).length === 0}>
                        {isImporting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Importing...</> : <><Check className="h-4 w-4 mr-2" /> Import as Bank Rate</>}
                      </Button>
                    </div>
                  </>
                )}
              </div>
            </DialogContent>
          </Dialog>

          {/* Add Rate Dialog */}
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button><Plus className="h-4 w-4 mr-2" /> Add Rate</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add Exchange Rate</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Currency Pair</Label>
                    <Select value={form.currency_pair} onValueChange={v => setForm(f => ({ ...f, currency_pair: v }))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {CURRENCY_PAIRS.map(cp => (
                          <SelectItem key={cp} value={cp}>{cp}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Rate Type</Label>
                    <Select value={form.rate_type} onValueChange={v => setForm(f => ({ ...f, rate_type: v }))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {RATE_TYPES.map(rt => (
                          <SelectItem key={rt.value} value={rt.value}>{rt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Effective Date</Label>
                    <Input
                      type="date"
                      value={form.effective_date}
                      onChange={e => setForm(f => ({ ...f, effective_date: e.target.value }))}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Rate ({quoteCurrency})</Label>
                    <Input
                      type="number"
                      step="0.000001"
                      min="0"
                      value={form.rate || ''}
                      onChange={e => setForm(f => ({ ...f, rate: parseFloat(e.target.value) || 0 }))}
                      placeholder={quoteCurrency === 'ETB' ? 'e.g. 153.50' : 'e.g. 0.92'}
                      required
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Notes (optional)</Label>
                  <Textarea
                    value={form.notes || ''}
                    onChange={e => setForm(f => ({ ...f, notes: e.target.value || null }))}
                    placeholder="e.g. CBE official selling rate"
                    rows={2}
                  />
                </div>
                <Button type="submit" disabled={isCreating || form.rate <= 0} className="w-full">
                  {isCreating ? 'Adding...' : 'Add Rate'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-3 flex-wrap items-end">
        <Select value={filterCurrency || 'all'} onValueChange={v => setFilterCurrency(v === 'all' ? '' : v)}>
          <SelectTrigger className="w-40"><SelectValue placeholder="Currency Pair" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Pairs</SelectItem>
            {CURRENCY_PAIRS.map(cp => (
              <SelectItem key={cp} value={cp}>{cp}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filterRateType || 'all'} onValueChange={v => setFilterRateType(v === 'all' ? '' : v)}>
          <SelectTrigger className="w-44"><SelectValue placeholder="Rate Type" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Rate Types</SelectItem>
            {RATE_TYPES.map(rt => (
              <SelectItem key={rt.value} value={rt.value}>{rt.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">From</Label>
          <Input type="date" className="w-40" value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
        </div>
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">To</Label>
          <Input type="date" className="w-40" value={dateTo} onChange={e => setDateTo(e.target.value)} />
        </div>
        {(dateFrom || dateTo || filterCurrency || filterRateType) && (
          <Button
            variant="ghost"
            onClick={() => { setDateFrom(''); setDateTo(''); setFilterCurrency(''); setFilterRateType(''); }}
          >
            Clear
          </Button>
        )}
      </div>


      {/* Horizontal Pivot Table */}
      <div className="border rounded-lg overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="sticky left-0 bg-background z-10 min-w-[160px]"></TableHead>
              {pivotData.sortedDates.map(date => (
                <TableHead key={date} className="text-center whitespace-nowrap text-xs min-w-[80px]">
                  {format(new Date(date), 'MMM d')}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={pivotData.sortedDates.length + 1} className="text-center py-8 text-muted-foreground">
                  Loading...
                </TableCell>
              </TableRow>
            ) : pivotData.sortedRows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={pivotData.sortedDates.length + 1} className="text-center py-8 text-muted-foreground">
                  No exchange rates found. Add your first rate to get started.
                </TableCell>
              </TableRow>
            ) : (
              pivotData.sortedRows.map(rowKey => (
                <TableRow key={rowKey}>
                  <TableCell className="font-medium sticky left-0 bg-background z-10 whitespace-nowrap">
                    {rowLabel(rowKey)}
                  </TableCell>
                  {pivotData.sortedDates.map(date => {
                    const cell = pivotData.rowMap.get(rowKey)?.get(date);
                    return (
                      <TableCell key={date} className="text-right font-mono text-sm whitespace-nowrap">
                        {cell ? cell.rate.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 6 }) : '—'}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
      {/* Bank Account Rate Assignment */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <Building2 className="h-5 w-5" />
          <h2 className="text-lg font-semibold">Bank Account Rate Assignment</h2>
        </div>
        <p className="text-muted-foreground text-sm">
          Assign which exchange rate type each bank account uses for currency conversion.
        </p>
        <div className="border rounded-lg">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Bank Account</TableHead>
                <TableHead>Currency</TableHead>
                <TableHead>Current Rate Type</TableHead>
                <TableHead>Change Rate Type</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {bankAccountsLoading ? (
                <TableRow>
                  <TableCell colSpan={4} className="text-center py-6 text-muted-foreground">Loading...</TableCell>
                </TableRow>
              ) : !bankAccountsList?.length ? (
                <TableRow>
                  <TableCell colSpan={4} className="text-center py-6 text-muted-foreground">No bank accounts found.</TableCell>
                </TableRow>
              ) : (
                bankAccountsList.map(account => (
                  <TableRow key={account.id}>
                    <TableCell className="font-medium">{account.name}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{account.currency || 'ETB'}</Badge>
                    </TableCell>
                    <TableCell>
                      <Badge className={
                        account.exchange_rate_type === 'competitive_rate'
                          ? 'bg-orange-100 text-orange-800 hover:bg-orange-100'
                          : 'bg-blue-100 text-blue-800 hover:bg-blue-100'
                      }>
                        {account.exchange_rate_type === 'competitive_rate' ? 'Competitive Rate' : 'Bank Rate'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Select
                        value={account.exchange_rate_type || 'bank_rate'}
                        onValueChange={(v) => handleRateTypeChange(account.id, v)}
                      >
                        <SelectTrigger className="w-44">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {RATE_TYPES.map(rt => (
                            <SelectItem key={rt.value} value={rt.value}>{rt.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
    </Layout>
  );
}
